import universesClient from '@modules/clients/universes';
import developClient from '@modules/clients/develop';
import { assetPermissionsApiClient } from '@modules/clients';
import { CreatorHomeReengagementClient } from '@modules/clients/creatorHome';
import unifiedLoggerClient from '@modules/eventStream/unifiedLoggerClient';
import { AssetGrantableAction, SubjectType } from '@rbx/clients/assetPermissionsApi';
import { urls } from '@modules/miscellaneous/common';
import { captureException } from '@sentry/nextjs';
import { Asset, AssetType } from '@modules/clients/assetsupload';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { CreateUniverseOperationResponse } from '@rbx/clients/universesApi';
import { FetchError, ResponseError } from '@rbx/clients-core';
import { yourPlaceThumbnail, yourPlaceIcon } from '../constants/assetConstants';

export const UniverseDescriptionTranslationKey = 'Description.YourPlaceDescription';

const UNIVERSE_DESCRIPTION = `Let's build together!
Create in the Roblox app or in Studio with Your Place.
Get started with Studio here: ${urls.creatorHub.docs.getSettingUpStudioUrl()}`;

export const isValidTemplateId = (templateId: number | null | undefined): templateId is number => {
  return templateId != null && templateId !== -1;
};

export interface CreateYourPlaceOptions {
  starterPlaceTemplateId: number;
  userId: number | undefined;
  uploadAssetForPlaceAsync: UseMutateAsyncFunction<
    Asset,
    Error,
    {
      placeId: number;
      userId: number;
      groupId?: number;
      isGroupUpload?: boolean;
      file: File;
      assetType: AssetType;
      price: number;
    },
    unknown
  >;
  uploadAutoGeneratedPlaceIconAsync: UseMutateAsyncFunction<
    void,
    Error,
    {
      autoGenIconImageUrl: string;
      placeId: number;
    },
    unknown
  >;
  placeDescription: string;
  onFailure?: (error: unknown) => void;
}

export const getRobloxLinkForRootPlaceId = (rootPlaceId: number): string => {
  return `roblox://experiences/start?placeId=${rootPlaceId}`;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
export const WithRetryFailure = Symbol('WithRetryFailure');

const backoffDelayUnknownError = (error: unknown) => {
  if (
    error instanceof FetchError &&
    error?.message ===
      'The request failed and the interceptors did not return an alternative response'
  ) {
    // give more backoff for this error
    return 500;
  }
  return 200;
};

/**
/**
 * Run an async function with retry logic and exponential backoff.
 *
 * @param fn - The async function to execute.
 * @param options.numAttempts - Maximum number of attempts (default: 3).
 * @param options.isRetryable - Function to determine if an error is retryable (default: always true).
 * @param options.attemptCount - Current attempt count (for internal use; default: 1).
 * @param options.throwOnError - Whether to throw the error after final attempt (default: false, returns failure symbol).
 * @returns A promise that resolves to the result of the function or the `failure` symbol if all retries are exhausted and throwOnError is false.
 */
export const withRetry = async <T>(
  fn: () => Promise<T>,
  {
    operationId = 'unknown',
    numAttempts = 3,
    isRetryable = () => true,
    attemptCount = 1,
    throwOnError = false,
  }: {
    operationId?: string;
    numAttempts?: number;
    isRetryable?: (error: unknown) => boolean;
    attemptCount?: number;
    throwOnError?: boolean;
  } = {},
): Promise<T | typeof WithRetryFailure> => {
  try {
    return await fn();
  } catch (error) {
    if (operationId !== 'unknown') {
      unifiedLoggerClient.logErrorEvent({
        eventName: 'createYourPlaceRequestError',
        parameters: {
          error: error instanceof Error ? error.message : 'Unknown error',
          attemptCount: attemptCount.toString(),
          operationId,
        },
      });
    }
    if (attemptCount < numAttempts && isRetryable(error)) {
      // exponential backoff; first retry waits 200ms, second 400ms, ... for "interceptor errors" backoff base is 500ms
      await delay(backoffDelayUnknownError(error) * 2 ** (attemptCount - 1));
      return withRetry(fn, {
        operationId,
        numAttempts,
        isRetryable,
        attemptCount: attemptCount + 1,
        throwOnError,
      });
    }
    if (throwOnError) {
      throw error;
    }
    return WithRetryFailure;
  }
};

/**
 * Creates a new place/universe with the specified template and configuration.
 * This function handles the complete flow of:
 * 1. Creating the universe from template
 * 2. Setting universe configuration
 * 3. Enabling Team Create
 * 4. Granting permissions
 * 5. Setting place configuration
 * 6. Creating reengagement entry
 * 7. Redirecting to the place
 */
export const createYourPlace = async ({
  starterPlaceTemplateId,
  userId,
  uploadAssetForPlaceAsync,
  uploadAutoGeneratedPlaceIconAsync,
  placeDescription,
  onFailure = () => {},
}: CreateYourPlaceOptions): Promise<void> => {
  try {
    if (!userId) {
      throw new Error('userId is undefined');
    }

    // Create the universe from template
    const response = (await withRetry(
      () =>
        universesClient.createUniverse({
          placesCreateUniverseRequest: {
            templatePlaceId: starterPlaceTemplateId,
            isPublish: true,
          },
        }),
      {
        operationId: 'createUniverse',
        numAttempts: 2,
        throwOnError: true,
        isRetryable: (error) => {
          if (error instanceof ResponseError) {
            // eslint-disable-next-line no-unsafe-optional-chaining -- if it's NaN it still fails our check
            if (error?.response?.status % 100 === 5) {
              return true;
            }
          }
          if (error instanceof Error) {
            if (error?.message?.match(/^Response from [^ ]+ returned an error code 500$/)) {
              return true;
            }
          }
          return false;
        },
      },
    )) as CreateUniverseOperationResponse;

    // p50 latency is ~427ms, wait 500ms to ensure the universe is created before updating it
    await delay(500);

    // Both universeId and rootPlaceId will always exist at this point
    const { universeId, rootPlaceId } = response;
    if (!universeId || !rootPlaceId) {
      throw new Error('Failed to get universe or place ID from response');
    }

    // Log the creation event
    unifiedLoggerClient.logClickEvent({
      eventName: 'yourPlaceCreationClicked',
      parameters: {
        template_place_id: starterPlaceTemplateId.toString(),
        universe_id: universeId.toString(),
      },
    });

    // Set universe configuration
    await withRetry(
      () =>
        developClient.setUniverseConfigurationV2(
          universeId,
          undefined, // name
          placeDescription || UNIVERSE_DESCRIPTION, // description
          undefined, // isStudioAccessToApisAllowed
          undefined, // MeshTextureApiAccessAllowed
          undefined, // allowPrivateServers
          undefined, // privateServerPrice
          true, // isFriendsOnly
        ),
      { operationId: 'setUniverseConfiguration' },
    );

    // Enable Team Create
    await withRetry(() => developClient.toggleTeamCreate(universeId, true));

    // Grant UpdateFromRcc permission
    await withRetry(
      () =>
        assetPermissionsApiClient.grantAssetPermissions(rootPlaceId, [
          {
            subjectType: SubjectType.Universe,
            subjectId: universeId.toString(),
            action: AssetGrantableAction.UpdateFromRcc,
          },
        ]),
      { operationId: 'grantAssetPermissions', throwOnError: true },
    );

    // Set place configuration
    await withRetry(
      () =>
        developClient.patchPlaceConfigurationInfo({
          placeId: rootPlaceId,
          _configuration: { maxPlayerCount: 50 },
        }),
      { operationId: 'patchPlaceConfigurationInfo' },
    );

    // Create YourPlace reengagement entry
    await withRetry(
      () => CreatorHomeReengagementClient.createYourPlaceReengagementEntry(universeId),
      { operationId: 'createYourPlaceReengagementEntry' },
    );

    try {
      if (userId) {
        const r = await fetch(yourPlaceThumbnail);
        if (!r.ok) {
          throw new Error('Failed to fetch yourPlaceThumbnail');
        }
        const blobFile = await r.blob();
        const file = new File([blobFile], 'autoGenThumbnail', { type: 'image/png' });

        try {
          // upload auto-generated thumbnail
          uploadAssetForPlaceAsync({
            placeId: rootPlaceId,
            userId,
            file,
            assetType: AssetType.Image,
            price: 0,
          });
        } catch (error) {
          const errorObj =
            error instanceof Error
              ? error
              : new Error(
                  'CreateYourPlace: Unknown error occurred uploading auto-generated thumbnail',
                );
          captureException(errorObj);
          unifiedLoggerClient.logErrorEvent({
            eventName: 'createYourPlaceRequestError',
            parameters: {
              error: errorObj.message,
              operationId: 'uploadAutoGeneratedThumbnail',
              template_place_id: starterPlaceTemplateId.toString(),
              attemptCount: '1',
            },
          });

          // swallow and don't block success path if uploading auto-generated thumbnail fails
        }

        try {
          // upload auto-generated icon
          uploadAutoGeneratedPlaceIconAsync({
            autoGenIconImageUrl: yourPlaceIcon,
            placeId: rootPlaceId,
          });
        } catch (error) {
          const errorObj =
            error instanceof Error
              ? error
              : new Error('CreateYourPlace: Unknown error occurred uploading auto-generated icon');
          captureException(errorObj);
          unifiedLoggerClient.logErrorEvent({
            eventName: 'createYourPlaceRequestError',
            parameters: {
              error: errorObj.message,
              operationId: 'uploadAutoGeneratedIcon',
              template_place_id: starterPlaceTemplateId.toString(),
              attemptCount: '1',
            },
          });
          // swallow and don't block success path if uploading auto-generated icon fails
        }
      }
    } catch {
      // swallow and don't block success path if either upload fails
    }

    await delay(500);

    // Redirect to deep link
    const deepLinkUrl = getRobloxLinkForRootPlaceId(rootPlaceId);
    window.location.href = deepLinkUrl;
  } catch (error) {
    const errorObj =
      error instanceof Error
        ? error
        : new Error('CreateYourPlace: Unknown error occurred creating place');
    captureException(errorObj);
    unifiedLoggerClient.logErrorEvent({
      eventName: 'createYourPlaceFailed',
      parameters: {
        error: errorObj.message,
        template_place_id: starterPlaceTemplateId.toString(),
      },
    });
    onFailure(errorObj);
  }
};
